import { useState, type FormEvent } from "react";
import { Link, getRouteApi, useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { resetPassword, type PasswordResetOutcome } from "../../shared/auth/password-reset";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { passwordResetFailureMessage } from "./ForgotPasswordPage";
import { PasswordInput } from "./PasswordInput";

/** Better Auth default minimum password length, the same as at registration. */
const MIN_PASSWORD_LENGTH = 8;

const routeApi = getRouteApi("/reset-password");

/**
 * Where the mailed reset link lands. The gateway checked the token before
 * redirecting here: a live one arrives as `?token=`, an expired or spent one as
 * `?error=INVALID_TOKEN`.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { token, error } = routeApi.useSearch();

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation<PasswordResetOutcome, never, { token: string; newPassword: string }>(
    {
      mutationFn: resetPassword,
      onSuccess: (outcome) => {
        if (outcome.status !== "done") return;
        // A full load, not a router navigation: the reset ended every session
        // of the account, and an app that resolved its auth gate while signed
        // in would otherwise keep showing the workspace until its next poll.
        window.location.replace(
          router.buildLocation({ to: "/sign-in", search: { passwordChanged: true } }).href,
        );
      },
    },
  );
  const outcome = mutation.data;

  if (token === undefined || error !== undefined || outcome?.status === "invalid-token") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("auth/thisLinkNoLonger_guhwi0")}</CardTitle>
            <CardDescription>{t("auth/theLinkHasExpired_2vndvh")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button render={<Link to="/forgot-password" />}>
              {t("auth/requestANewLink_777km9")}
            </Button>
            <p className="text-muted-foreground text-sm">
              <Link className="text-primary underline-offset-4" to="/sign-in">
                {t("auth/backToSignIn_x0t0pf")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </main>
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(t("auth/passwordMustBeAt_7huopp"));
      return;
    }
    if (password !== confirmation) {
      setValidationError(t("auth/thePasswordsDoNot_iup4gq"));
      return;
    }
    setValidationError(null);
    mutation.mutate({ token, newPassword: password });
  };

  const errorMessage =
    validationError ??
    (outcome !== undefined && outcome.status !== "done"
      ? passwordResetFailureMessage(outcome, t)
      : null);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("auth/setANewPassword_a5asg2")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-password-new">{t("auth/newPassword_1c4uo1")}</Label>
              <PasswordInput
                id="reset-password-new"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-password-confirm">
                {t("auth/repeatTheNewPassword_9751un")}
              </Label>
              <PasswordInput
                id="reset-password-confirm"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </div>
            {errorMessage ? (
              <p className="text-destructive text-sm" role="alert">
                {errorMessage}
              </p>
            ) : null}
            <Button type="submit" disabled={mutation.isPending || mutation.data?.status === "done"}>
              {mutation.isPending ? t("auth/saving_t83mqe") : t("auth/savePassword_lekubw")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
